import clsx from 'clsx';
import React from 'react';
import { Skeleton } from './Feedback';
import { HUE_TINT, type Hue } from './hue';

/**
 * Figures summary (KPI strip): a page-level row of figures in one card,
 * hairlines between the cells. Each figure is an uppercase micro-label, a
 * tabular value and an optional quiet second line (an all-time total, a
 * period, a comparison). An optional icon chip on the label row, in the
 * figure's categorical hue, keeps the same number the same colour on every
 * page. The chip is small so five or six figures still fit a row.
 *
 * `Figure` stays the inline toolbar figure and `SummaryFacts` the figures
 * in a list page's header band; this is the block form for dashboards.
 */

export interface SummaryFigureProps {
  label: string;
  value: React.ReactNode;
  /** Shown in place of the value, at its height (e.g. a "Connect" link). */
  action?: React.ReactNode;
  /** Quiet second line ("1,204 all time"). Text only — no colour, no arrows. */
  secondary?: React.ReactNode;
  /** Extra line under the figure, such as an estimate note. */
  footer?: React.ReactNode;
  /** Adornment on the label row (e.g. `<Proposed />`). */
  adornment?: React.ReactNode;
  /**
   * Icon chip before the label, tinted with `hue` (default accent).
   * `neutral` greys it out for a figure that is not live yet.
   */
  icon?: React.ReactNode;
  hue?: Hue | 'neutral';
  className?: string;
}

// Every cell draws its own top and left hairline and is shifted onto the
// previous one, so wrapped rows never show a double rule and the card's
// own border is the outer edge.
const cell =
  'flex min-w-0 gap-3.5 border-l border-t border-v2-line -ml-px -mt-px px-5 py-4 lg:py-5';

export const SummaryFigure: React.FC<SummaryFigureProps> = ({
  label,
  value,
  action,
  secondary,
  footer,
  adornment,
  icon,
  hue = 'accent',
  className,
}) => (
  <div role="group" aria-label={label} className={clsx(cell, className)}>
    <div className="flex min-w-0 flex-col gap-0.5">
      <div className="flex items-center gap-1.5">
        {icon && (
          <span
            aria-hidden
            className={clsx(
              'mr-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-[7px]',
              hue === 'neutral'
                ? 'bg-v2-sunken text-v2-fg-4 ring-1 ring-inset ring-v2-line'
                : HUE_TINT[hue],
            )}
          >
            {icon}
          </span>
        )}
        <span className="text-v2-11 font-semibold uppercase tracking-[0.08em] text-v2-fg-3 sm:truncate">
          {label}
        </span>
        {adornment}
      </div>
      {action ? (
        <div className="flex h-8 items-center lg:h-[34px]">{action}</div>
      ) : (
        <span className="v2-num truncate text-v2-24 font-bold tracking-[-0.03em] text-v2-fg lg:text-v2-28">
          {value}
        </span>
      )}
      <span className="v2-num truncate text-v2-12 text-v2-fg-3">
        {secondary ?? '\u00a0'}
      </span>
      {footer && (
        <div className="mt-1 text-v2-11 leading-snug text-v2-fg-3">
          {footer}
        </div>
      )}
    </div>
  </div>
);

export const SummaryFigureSkeleton: React.FC<{
  /** Reserve the label row's icon chip. */
  icon?: boolean;
  className?: string;
}> = ({ icon, className }) => (
  <div className={clsx(cell, className)} aria-hidden>
    <div className="flex flex-col gap-2 py-0.5">
      {icon ? (
        <div className="flex items-center gap-2">
          <Skeleton className="h-6 w-6 rounded-[7px]" />
          <Skeleton width={72} className="h-2.5" />
        </div>
      ) : (
        <Skeleton width={72} className="h-2.5" />
      )}
      <Skeleton width={96} className="h-7" />
      <Skeleton width={84} className="h-2.5" />
    </div>
  </div>
);

export type FiguresColumns = 2 | 3 | 4 | 5 | 6;

/**
 * Wide-screen column count for `n` figures: 2–6. Past six, the figures wrap
 * onto as few rows as fit and spread evenly across them (7 → 4 + 3).
 */
export const figuresColumns = (n: number): FiguresColumns => {
  const rows = Math.max(1, Math.ceil(n / 6));
  return Math.min(6, Math.max(2, Math.ceil(n / rows))) as FiguresColumns;
};

/**
 * The strip. Children are `SummaryFigure`s (or skeletons). Two columns below
 * `sm`, three to `lg`, then `columns` (default: one per child, 2–6).
 * `header` is a full-width row above the figures that names their period.
 */
export const FiguresSummary: React.FC<{
  children: React.ReactNode;
  header?: React.ReactNode;
  columns?: FiguresColumns;
  className?: string;
  'aria-label'?: string;
  'aria-busy'?: boolean;
}> = ({ children, header, columns, className, ...rest }) => {
  const cols = columns ?? figuresColumns(React.Children.count(children));
  return (
    <div
      role="group"
      aria-label={rest['aria-label']}
      aria-busy={rest['aria-busy'] || undefined}
      className={clsx(
        'grid overflow-clip rounded-v2-xl border border-v2-line bg-v2-surface shadow-v2-card',
        cols === 2 ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-3',
        cols === 4 && 'lg:grid-cols-4',
        cols === 5 && 'lg:grid-cols-5',
        cols === 6 && 'lg:grid-cols-6',
        className,
      )}
    >
      {header && (
        <div className="col-span-full flex min-w-0 flex-wrap items-baseline gap-x-2.5 gap-y-0.5 px-5 py-3">
          {header}
        </div>
      )}
      {children}
    </div>
  );
};
